/**
 * Vendor Commission Invoices
 * Read-only view of the current vendor's monthly commission invoices
 * generated by the marketplace admin.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import {
  Receipt, ChevronDown, ChevronUp, RefreshCw,
  Loader2, AlertCircle, FileText, ExternalLink, Download,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input }  from '../../components/ui/input';
import { Badge }  from '../../components/ui/badge';
import { toast }  from '../../components/ui/sonner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function monthLabel(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(parseInt(y), parseInt(mo) - 1)
    .toLocaleString('default', { month: 'long', year: 'numeric' });
}

const STATUS_STYLES = {
  finalized: 'bg-blue-100 text-blue-700',
  issued:    'bg-indigo-100 text-indigo-700 font-semibold',
  sent:      'bg-green-100 text-green-700',
  paid:      'bg-emerald-100 text-emerald-800 font-semibold',
  draft:     'bg-gray-100 text-gray-600',
};

function StatusPill({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  );
}

// ─── Line items table ──────────────────────────────────────────────────────────

function LineItemsTable({ items = [] }) {
  if (!items || items.length === 0) {
    return <p className="text-xs text-gray-400 px-4 py-3">No line items available.</p>;
  }
  return (
    <table className="w-full text-xs mt-2" data-testid="line-items-table">
      <thead>
        <tr className="bg-gray-50 text-gray-500 text-left">
          <th className="py-2 px-3 font-medium">Order</th>
          <th className="py-2 px-3 font-medium text-right">Sale</th>
          <th className="py-2 px-3 font-medium text-right">Rate</th>
          <th className="py-2 px-3 font-medium text-right">Commission</th>
          <th className="py-2 px-3 font-medium text-right">GST 18%</th>
          <th className="py-2 px-3 font-medium text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {items.map((li, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
            <td className="py-2 px-3 text-gray-700">
              #{li.order_number || li.order_id?.slice(0, 8)}
            </td>
            <td className="py-2 px-3 text-right text-gray-600">
              {fmt(li.gross_total)}
              {li.commission_base != null && Math.abs(li.commission_base - li.gross_total) >= 0.01 && (
                <div className="text-[10px] text-amber-600 font-normal" title="Commission is calculated on product value only, excluding shipping/pass-through">
                  on {fmt(li.commission_base)} product value
                </div>
              )}
            </td>
            <td className="py-2 px-3 text-right text-gray-500">{li.commission_rate_pct?.toFixed(1)}%</td>
            <td className="py-2 px-3 text-right text-gray-700">{fmt(li.commission)}</td>
            <td className="py-2 px-3 text-right text-amber-700">{fmt(li.commission_gst)}</td>
            <td className="py-2 px-3 text-right font-semibold text-gray-900">{fmt(li.line_total)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-gray-200">
          <td colSpan={3} className="py-2 px-3 text-xs text-gray-400">SAC: 996211</td>
          <td className="py-2 px-3 text-right font-semibold text-gray-800 text-xs">
            {fmt(items.reduce((s, l) => s + l.commission, 0))}
          </td>
          <td className="py-2 px-3 text-right font-semibold text-amber-700 text-xs">
            {fmt(items.reduce((s, l) => s + l.commission_gst, 0))}
          </td>
          <td className="py-2 px-3 text-right font-bold text-gray-900 text-xs">
            {fmt(items.reduce((s, l) => s + l.line_total, 0))}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

// ─── Merchant Credit / Debit Note card (vendor read-only view) ─────────────────
// Mirrors `CreditNoteCard` in pages/admin/CommissionInvoicesPage.jsx — same
// visual language (orange border + red badge for a Credit Note that reduces
// what the vendor owes; emerald border + green badge for a Debit Note that
// adds commission that was omitted from an already-locked invoice), but this
// is a read-only vendor view (no admin actions).

function CreditNoteCard({ cn, onPreview, onDownload }) {
  const [downloading, setDownloading] = useState(false);
  const isDebit = cn.document_type === 'debit_note';

  async function handleDownload() {
    setDownloading(true);
    await onDownload(cn.id, cn.credit_note_number);
    setDownloading(false);
  }

  return (
    <div
      className={`border rounded-xl bg-white overflow-hidden ${isDebit ? 'border-emerald-100' : 'border-orange-100'}`}
      data-testid={`vendor-credit-note-card-${cn.id}`}
    >
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-gray-900" data-testid="vendor-credit-note-number">
              {cn.credit_note_number}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isDebit ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {isDebit ? 'Debit' : 'Credit'}
            </span>
            {cn.original_invoice_late_declared && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700 font-medium">
                Original Late-Declared
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-0.5">
            Against invoice <span className="font-mono">{cn.original_invoice_number}</span>
            <span className="text-gray-400 ml-2 text-xs">({monthLabel(cn.original_month)})</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Issued {monthLabel(cn.month)} · Order #{(cn.order_id || '').slice(0, 8)}
          </p>
          {cn.reason && <p className="text-xs text-gray-400 mt-1">{cn.reason}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-400">{isDebit ? 'Additional Due' : 'Credit Amount'}</p>
          <p className={`text-lg font-bold ${isDebit ? 'text-emerald-700' : 'text-red-700'}`} data-testid="vendor-credit-note-total">
            {isDebit ? '+' : '-'}{fmt(Math.abs(cn.grand_total))}
          </p>
        </div>
      </div>

      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          onClick={() => onPreview(cn.id)}
          data-testid="vendor-preview-credit-note-btn"
        >
          <FileText className="h-3 w-3 mr-1" />
          Preview
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="vendor-download-credit-note-btn"
        >
          {downloading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
          Download
        </Button>
      </div>
    </div>
  );
}

// ─── Single invoice card ───────────────────────────────────────────────────────

function InvoiceCard({ inv }) {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(null); // null | 'invoice' | 'advice'
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  // iter-301f — was a plain `<a href={apiUrl} target="_blank">`, which
  // navigates with NO Authorization header (browser tab navigation can't
  // carry a Bearer token) — the endpoint always rejected it with 401
  // "Could not validate credentials", so vendors could never preview
  // their own invoice. Fetch with the auth header first, then open the
  // fetched HTML as a blob: URL (renders correctly, not a file download).
  // iter-242 — generalised to `path` so the same helper drives both the
  // Tax Invoice and the new Payment Advice / Settlement Note document.
  const previewDoc = async (path, errorMsg) => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errorMsg);
    }
  };

  // A genuine separate Download — hits the dedicated `/pdf` endpoint
  // (same ownership rule as `/html`) and saves a real file, distinct
  // from Preview above which only opens a rendered blob in a new tab.
  const downloadDoc = async (path, filename, key, errorMsg) => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    setDownloading(key);
    try {
      const response = await fetch(`${BACKEND}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorMsg);
    } finally {
      setDownloading(null);
    }
  };

  const previewInvoice = () => previewDoc(
    `/api/admin/commission-invoices/${inv.id}/html`, 'Failed to load invoice preview',
  );
  const downloadInvoice = () => downloadDoc(
    `/api/admin/commission-invoices/${inv.id}/pdf`, `${inv.invoice_number || inv.id}.pdf`,
    'invoice', 'Failed to download invoice PDF',
  );
  // Payment Advice — the companion Settlement Note showing this invoice
  // was settled via deduction from the vendor's payout, not a separate
  // cash/bank payment (see backend `_render_payment_advice_html`).
  const previewAdvice = () => previewDoc(
    `/api/admin/commission-invoices/${inv.id}/payment-advice/html`, 'Failed to load payment advice preview',
  );
  const downloadAdvice = () => downloadDoc(
    `/api/admin/commission-invoices/${inv.id}/payment-advice/pdf`,
    `${inv.payment_advice_number || 'payment-advice'}.pdf`, 'advice', 'Failed to download payment advice PDF',
  );

  return (
    <div
      className="border border-gray-200 rounded-xl bg-white overflow-hidden"
      data-testid={`commission-invoice-${inv.id}`}
    >
      {/* Header row */}
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-gray-900" data-testid="invoice-number">
              {inv.invoice_number}
            </span>
            <StatusPill status={inv.status} />
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {monthLabel(inv.month)} · {inv.order_count} order{inv.order_count !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Totals */}
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-400">Commission + GST</p>
          <p className="text-lg font-bold text-gray-900" data-testid="invoice-grand-total">
            {fmt(inv.grand_total)}
          </p>
          <p className="text-xs text-gray-400">
            {fmt(inv.subtotal_commission)} + {fmt(inv.subtotal_gst)} GST
          </p>
        </div>
      </div>

      {/* Payment link (read-only) */}
      {inv.payment_link_url && (
        <div className="px-5 pb-3 flex items-center gap-2">
          <a
            href={inv.payment_link_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 flex items-center gap-1"
            data-testid="payment-link"
          >
            <ExternalLink className="h-3 w-3" />
            Pay Now
          </a>
          <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
            inv.payment_link_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {inv.payment_link_status || 'pending'}
          </span>
        </div>
      )}

      {/* Action bar */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          onClick={() => setExpanded(!expanded)}
          data-testid="toggle-line-items-btn"
        >
          {expanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          {expanded ? 'Hide' : 'View'} Line Items ({inv.order_count})
        </Button>

        <button
          onClick={previewInvoice}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-200 text-xs text-gray-600 hover:bg-gray-100 transition-colors h-7"
          data-testid="preview-html-link"
        >
          <FileText className="h-3 w-3" />
          Preview Invoice
        </button>

        <button
          onClick={downloadInvoice}
          disabled={downloading === 'invoice'}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-200 text-xs text-gray-600 hover:bg-gray-100 transition-colors h-7 disabled:opacity-60"
          data-testid="download-invoice-pdf-btn"
        >
          {downloading === 'invoice' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Download PDF
        </button>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        <button
          onClick={previewAdvice}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-emerald-200 text-xs text-emerald-700 hover:bg-emerald-50 transition-colors h-7"
          data-testid="preview-payment-advice-btn"
          title="Settlement Note confirming this invoice was deducted from your payout"
        >
          <FileText className="h-3 w-3" />
          Preview Payment Advice
        </button>

        <button
          onClick={downloadAdvice}
          disabled={downloading === 'advice'}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-emerald-200 text-xs text-emerald-700 hover:bg-emerald-50 transition-colors h-7 disabled:opacity-60"
          data-testid="download-payment-advice-btn"
        >
          {downloading === 'advice' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Download Advice
        </button>
      </div>

      {/* Expanded line items */}
      {expanded && (
        <div className="border-t border-gray-100 overflow-x-auto">
          <LineItemsTable items={inv.line_items || []} />
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function VendorCommissionInvoices() {
  const { apiCall } = useAuth();

  const [month,        setMonth]        = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [invoices,     setInvoices]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  // Tabs: "Invoices" (existing) + "Credit Notes" (new — vendor-side view of
  // Merchant Credit/Debit Notes, backend already scopes these to
  // current_user.id for a vendor via `/api/admin/commission-invoices/credit-notes`).
  const [activeTab, setActiveTab] = useState('invoices');
  const [creditNotes,        setCreditNotes]        = useState([]);
  const [creditNotesLoading, setCreditNotesLoading]  = useState(false);
  const [creditNotesError,   setCreditNotesError]    = useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (month)        params.set('month',  month);
      if (statusFilter) params.set('status', statusFilter);
      const data = await apiCall(`/api/admin/commission-invoices/my-invoices?${params}`);
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [month, statusFilter]);

  const loadCreditNotes = useCallback(async () => {
    setCreditNotesLoading(true);
    setCreditNotesError(null);
    try {
      const data = await apiCall('/api/admin/commission-invoices/credit-notes');
      setCreditNotes(Array.isArray(data) ? data : []);
    } catch (e) {
      setCreditNotesError(e?.message || 'Failed to load credit notes');
    } finally {
      setCreditNotesLoading(false);
    }
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);
  useEffect(() => { loadCreditNotes(); }, [loadCreditNotes]);

  async function previewCreditNote(id) {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/credit-notes/${id}/html`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to load credit note preview');
    }
  }

  async function downloadCreditNotePdf(id, number) {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/credit-notes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download credit note PDF');
    }
  }

  // Summary
  const totalCommission = invoices.reduce((s, i) => s + (i.subtotal_commission || 0), 0);
  const totalGst        = invoices.reduce((s, i) => s + (i.subtotal_gst || 0), 0);
  const totalDue        = invoices.reduce((s, i) => s + (i.grand_total || 0), 0);

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50" data-testid="vendor-commission-invoices-page">

        {/* Page header */}
        <div className="bg-white border-b border-gray-200 px-6 py-5">
          <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Receipt className="h-5 w-5 text-gray-600" />
                Commission Invoices
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Monthly commission + GST charged by the marketplace
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={activeTab === 'credit_notes' ? loadCreditNotes : loadInvoices}
              disabled={activeTab === 'credit_notes' ? creditNotesLoading : loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${(activeTab === 'credit_notes' ? creditNotesLoading : loading) ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Tabs — "Invoices" (existing) + "Credit Notes" (new) */}
          <div className="max-w-4xl mx-auto flex gap-2 mt-4" data-testid="vendor-commission-tabs">
            {[
              { id: 'invoices',     label: 'Invoices',      count: invoices.length },
              { id: 'credit_notes', label: 'Credit Notes',  count: creditNotes.length },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  activeTab === t.id
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`vendor-commission-tab-${t.id}`}
              >
                {t.label} {t.count > 0 && <span className="opacity-70">({t.count})</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-6 space-y-5">

        {activeTab === 'invoices' && (
          <>
          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Month</label>
              <Input
                type="month"
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="h-9 text-sm w-44"
                data-testid="month-filter"
              />
            </div>
            {month && (
              <button
                onClick={() => setMonth('')}
                className="text-xs text-gray-400 hover:text-gray-600 underline self-end mb-2"
              >
                Clear month
              </button>
            )}
          </div>

          {/* Status pills */}
          <div className="flex gap-2 flex-wrap">
            {['', 'finalized', 'sent', 'paid'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  statusFilter === s
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`status-filter-${s || 'all'}`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>

          {/* Summary stats */}
          {invoices.length > 0 && (
            <div className="grid grid-cols-3 gap-4" data-testid="summary-stats">
              {[
                { label: 'Total Commission', value: fmt(totalCommission), sub: 'before GST' },
                { label: 'GST @ 18%',        value: fmt(totalGst),        sub: 'SAC 996211'   },
                { label: 'Total Due',         value: fmt(totalDue),        sub: `${invoices.length} invoices` },
              ].map(s => (
                <div key={s.label} className="bg-white border border-gray-200 rounded-xl px-4 py-4">
                  <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{s.value}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4"
                 data-testid="error-state">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && invoices.length === 0 && (
            <div className="text-center py-16 text-gray-400" data-testid="empty-state">
              <Receipt className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">
                {month ? `No invoices for ${monthLabel(month)}` : 'No commission invoices yet'}
              </p>
              <p className="text-xs mt-1">
                Invoices are generated monthly by the marketplace admin.
              </p>
            </div>
          )}

          {/* Invoice list */}
          <div className="space-y-3">
            {invoices.map(inv => (
              <InvoiceCard key={inv.id} inv={inv} />
            ))}
          </div>
          </>
        )}

        {activeTab === 'credit_notes' && (
          <>
          {creditNotesLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}

          {creditNotesError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4"
                 data-testid="vendor-credit-notes-error-state">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {creditNotesError}
            </div>
          )}

          {!creditNotesLoading && !creditNotesError && creditNotes.length === 0 && (
            <div className="text-center py-16 text-gray-400" data-testid="vendor-credit-notes-empty">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No credit notes issued</p>
              <p className="text-xs mt-1">
                Issued automatically when a return or cancellation crosses into a
                later month than the original invoice.
              </p>
            </div>
          )}

          {!creditNotesLoading && !creditNotesError && creditNotes.length > 0 && (
            <div className="space-y-3" data-testid="vendor-credit-notes-list">
              {creditNotes.map(cn => (
                <CreditNoteCard
                  key={cn.id}
                  cn={cn}
                  onPreview={previewCreditNote}
                  onDownload={downloadCreditNotePdf}
                />
              ))}
            </div>
          )}
          </>
        )}

        </div>
      </div>
    </DashboardLayout>
  );
}
